export type SaveState = 'idle' | 'saving' | 'saved' | 'error'

// This queue outlives the editor; revisions and identity fences protect late completions.
export class Autosave<T, R> {
  private pending: { value: T; revision: number } | null = null
  private revision = 0
  private timer: ReturnType<typeof setTimeout> | null = null
  private running: Promise<Awaited<R>> | null = null
  private due = false
  private listeners = new Set<() => void>()
  private state: SaveState = 'idle'
  private latest: T | undefined
  private dirty = false
  private failed = false
  private lastError: unknown
  private acknowledged: { value: T } | null = null

  constructor(private save: (value: T, previous: T | undefined) => R, private allowed: () => boolean, private onError: (error: unknown) => void) {}
  bind(save: (value: T, previous: T | undefined) => R, onError: (error: unknown) => void) { this.save = save; this.onError = onError }
  initialize(value: T) { if (!this.dirty && !this.running) this.acknowledged = { value } }
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener) } }
  snapshot = (): SaveState => this.state
  error = (): unknown => this.lastError
  recovery = (): T | undefined => this.dirty ? this.latest : undefined
  private emit(state: SaveState) { this.state = state; for (const listener of this.listeners) listener() }
  private assertAllowed() { if (!this.allowed()) throw new Error('Die Änderungen gehören nicht zum aktiven Arbeitsbereich.') }

  schedule(value: T, delay: number) {
    this.latest = value
    this.dirty = true
    this.pending = { value, revision: ++this.revision }
    if (this.timer) clearTimeout(this.timer)
    this.emit('saving')
    this.timer = setTimeout(() => { this.timer = null; void this.flush()?.catch(() => {}) }, delay)
  }

  flush = (retryFailed = true): Promise<Awaited<R>> | undefined => {
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
    if (!this.pending && !this.dirty && !this.running) return undefined
    try { this.assertAllowed() } catch (error) {
      this.lastError = error
      this.failed = true
      this.emit('error')
      return Promise.reject(error)
    }
    if (!this.pending && !this.running && this.failed && this.dirty && retryFailed) this.pending = { value: this.latest as T, revision: this.revision }
    if (!this.pending) return this.running ?? undefined
    this.due = true
    if (this.running) return this.running
    this.emit('saving')
    const run = async (): Promise<Awaited<R>> => {
      let result: Awaited<R> | undefined
      let failure: { error: unknown } | undefined
      while (this.pending && this.due) {
        const target = this.pending
        this.pending = null
        this.due = false
        try {
          this.assertAllowed()
          result = await this.save(target.value, this.acknowledged?.value)
          this.assertAllowed()
          this.acknowledged = { value: target.value }
          this.failed = false
          this.lastError = undefined
          if (this.revision === target.revision) this.dirty = false
          this.emit(this.dirty ? 'saving' : 'saved')
        } catch (error) {
          this.lastError = error
          const notify = !this.failed
          this.failed = true
          this.dirty = true
          this.emit('error')
          if (notify && this.allowed()) this.onError(error)
          if (!this.pending || !this.due) throw error
          failure ??= { error }
        }
      }
      if (failure) throw failure.error
      return result as Awaited<R>
    }
    this.running = run()
    const current = this.running
    const settle = () => {
      if (this.running === current) this.running = null
      if (this.pending && this.due) void this.flush()?.catch(() => {})
    }
    void current.then(settle, settle)
    return current
  }
}
